/**
 * `/database/editor` and `/database/editor/$table` — RO-3-11 (#44304), part 2 of 4.
 *
 * Port of `excellent/app/database/editor/page.tsx` (23 LOC over `_empty.tsx`, 36)
 * and `.../editor/[table]/page.tsx` (47 over `_client.tsx`, 62).
 *
 * Both pages exist to do ONE server read — `listObjects()` — and then hand off.
 * `schema.objects` is already mounted (RO-1-05), so this pair needed no new
 * endpoint and the row editor is live: `DatabaseGrid` drives `rows.list` /
 * `rows.insert` / `rows.update` / `rows.delete`, all four mounted, with the same
 * read-only-by-default write gate.
 *
 * ── Two control-flow translations, both load-bearing ──
 *
 * The index page did `redirect(...)` from a server render and the table page did
 * `notFound()`. Neither has an equivalent here, and the honest translation differs
 * per case:
 *
 *   · the index's redirect is DATA-DEPENDENT (it needs the object list before it
 *     knows where to go), so it cannot move into `beforeLoad` without the route
 *     also owning a loader. It stays in the component as a `navigate({replace})`
 *     effect — the user-visible behaviour is identical because the read resolves
 *     before first paint in practice, and the empty state it would otherwise show
 *     is the correct thing to render while it resolves.
 *   · the table page's `notFound()` becomes an inline "unknown object" state. An
 *     SPA has no framework 404 boundary, and inventing one for a single route
 *     would be a shell-level decision this item does not get to make. The guard
 *     itself is preserved exactly: an unknown name never reaches `DatabaseGrid`.
 */

import * as React from "react";
import { z } from "zod";
import { createRoute } from "@tanstack/react-router";
import { EmptyState } from "@excellent/ui";
import { useApi } from "@excellent/ui/api";
import { schema } from "@excellent/ui/api/contracts/database/schema";
import { rootRoute } from "./__root";
import { routeSearchSchema } from "../shell/search";
import { EditorEmptyState } from "./database/editor-empty";
import { TableEditorClient } from "./database/table-editor-client";

// ── /database/editor — bounce to the first user table, or offer to create one ─

function DatabaseEditorIndex() {
  const { data } = useApi(schema.objects, {});
  const navigate = databaseEditorIndexRoute.useNavigate();
  const firstTable = data?.find((o) => o.type === "table" && !o.system);

  React.useEffect(() => {
    if (!firstTable) return;
    void navigate({
      to: "/database/editor/$table",
      params: { table: firstTable.name },
      replace: true,
    });
  }, [firstTable, navigate]);

  // Rendered while the read resolves AND as the terminal state when there is
  // genuinely no user table — which is what the original showed too.
  return (
    <div className="flex flex-col">
      <div className="px-6 py-8">{firstTable ? null : <EditorEmptyState />}</div>
    </div>
  );
}

export const databaseEditorIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/database/editor",
  component: DatabaseEditorIndex,
});

// ── /database/editor/$table — the row editor ─────────────────────────────────

/**
 * `fkcol` / `fkval` — the foreign-key drill-through the inventory and the grid's
 * own FK cells link with. Route-local by construction: `routeSearchSchema` would
 * refuse to compile if either name were shell-wide, which is the guard that makes
 * declaring them safe rather than a grep-and-hope (`shell/search.ts`).
 */
const editorSearchSchema = routeSearchSchema({
  /** Column to pre-filter on, arriving from an FK cell elsewhere in the section. */
  fkcol: z.string().optional(),
  /** Value for `fkcol`. Both must be present for a filter to apply. */
  fkval: z.string().optional(),
});

function DatabaseTableEditor() {
  const { table } = databaseTableEditorRoute.useParams();
  const search = databaseTableEditorRoute.useSearch();
  const { data, error, isLoading } = useApi(schema.objects, {});

  const initialFilters =
    search.fkcol && search.fkval !== undefined ? { [search.fkcol]: search.fkval } : undefined;

  // Guard: only known objects. Preserved verbatim from the server page; the
  // rendering of the refusal is what changed, not the refusal.
  const known = data?.some((o) => o.name === table) ?? null;

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-8 px-6 py-8">
        {error ? (
          <p className="type-body text-destructive">{error.message ?? String(error)}</p>
        ) : null}
        {isLoading && !data ? <p className="type-body text-muted-foreground">Loading…</p> : null}
        {known === false ? (
          <EmptyState
            title={`No object named “${table}”`}
            description="It may have been dropped or renamed. Pick one from the inventory."
          />
        ) : null}
        {data && known ? (
          <TableEditorClient table={table} objects={data} initialFilters={initialFilters} />
        ) : null}
      </div>
    </div>
  );
}

export const databaseTableEditorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/database/editor/$table",
  validateSearch: editorSearchSchema,
  component: DatabaseTableEditor,
});
